<template>
  <div class="mt-12 md:relative">
    <div
      ref="wrapper"
      class="flex flex-col gap-12 md:flex-row md:flex-nowrap md:items-center md:overflow-hidden"
    >
      <TradeInChip />

      <BackUpChip />
    </div>

    <div
      v-if="shouldShowPrevButton"
      class="absolute left-0 top-[-2px] z-[1] hidden w-[104px] items-center justify-start md:flex"
      :class="previousGradientClasses"
    >
      <RevButtonIcon
        :alternative-text="i18n(translations.less)"
        :icon="IconChevronLeft"
        variant="secondary"
        @click="onPrevClick"
      />
    </div>

    <div
      v-if="shouldShowNextButton"
      class="absolute right-0 top-[-2px] z-[1] hidden w-[104px] items-center justify-end md:flex"
      :class="nextGradientClasses"
    >
      <RevButtonIcon
        :alternative-text="i18n(translations.more)"
        :icon="IconChevronRight"
        variant="secondary"
        @click="onNextClick"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { tw } from '@backmarket/utils/string/tw'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { IconChevronLeft } from '@ds/icons/IconChevronLeft'
import { IconChevronRight } from '@ds/icons/IconChevronRight'

import translations from '~/scopes/product/components/AdvertisementChips/AdvertisementChips.translations'
import TradeInChip from '~/scopes/product/components/AdvertisementChips/components/TradeInChip/TradeInChip.vue'
import BackUpChip from '~/scopes/product/pages/Product-JP/components/AdvertisementChips/components/BackUpChip/BackUpChip.vue'

const i18n = useI18n()
const wrapper = ref<HTMLElement | null>()
const displayedButton = ref(0)
const shouldShowPrevButton = ref(false)
const shouldShowNextButton = ref(false)

const previousGradientClasses = tw`bg-[linear-gradient(to_left,rgba(255,255,255,0)_9.09%,rgba(255,255,255,0.29)_27.86%,#fff_58.7%)]`
const nextGradientClasses = tw`bg-[linear-gradient(to_right,rgba(255,255,255,0)_9.09%,rgba(255,255,255,0.29)_27.86%,#fff_58.7%)]`

function updateShouldDisplayPrevButton() {
  if (wrapper.value) {
    shouldShowPrevButton.value =
      wrapper.value.scrollWidth > wrapper.value.offsetWidth &&
      displayedButton.value > 0
  }
}

function updateShouldDisplayNextButton() {
  const buttonsLength = wrapper.value?.querySelectorAll('button').length || 0
  const linkLength = wrapper.value?.querySelectorAll('a').length || 0

  const interactiveElementLength = buttonsLength + linkLength

  if (wrapper.value && interactiveElementLength) {
    shouldShowNextButton.value =
      wrapper.value.scrollWidth > wrapper.value.offsetWidth &&
      displayedButton.value < interactiveElementLength - 1
  }
}

function scrollTo(index: number) {
  if (wrapper.value) {
    const arrayOfButtons = Array.from(wrapper.value.querySelectorAll('button'))
    const arrayOfLinks = Array.from(wrapper.value.querySelectorAll('a'))

    const items = [...arrayOfButtons, ...arrayOfLinks]
    items[index].scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
    displayedButton.value = index
  }

  updateShouldDisplayPrevButton()
  updateShouldDisplayNextButton()
}

function onPrevClick() {
  scrollTo(displayedButton.value - 1)
}

function onNextClick() {
  scrollTo(displayedButton.value + 1)
}

onMounted(() => {
  updateShouldDisplayNextButton()
  window.addEventListener('resize', updateShouldDisplayPrevButton)
  window.addEventListener('resize', updateShouldDisplayNextButton)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateShouldDisplayPrevButton)
  window.removeEventListener('resize', updateShouldDisplayNextButton)
})
</script>
